import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import cookieService, { COOKIE_CATEGORIES } from "../../services/cookieService";

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({
    functional: true,
    analytics: false
  });

  useEffect(() => {
    if (!cookieService.hasConsent()) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    cookieService.acceptAll();
    setVisible(false);
    window.location.reload(); // Reload to apply preferences
  };

  const handleAcceptSelected = () => {
    cookieService.acceptSelected(selectedCategories);
    setVisible(false);
    window.location.reload(); // Reload to apply preferences
  };

  const handleRejectAll = () => {
    cookieService.rejectAll();
    setVisible(false);
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full bg-white dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-700 p-4 md:p-6 z-[10001] shadow-2xl">
      <div className="max-w-7xl mx-auto">
        {!showDetails ? (
          // Simple view
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('cookies.banner.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('cookies.banner.description')}
                <Link to="/datenschutz" className="text-blue-600 dark:text-blue-400 hover:underline ml-1">
                  {t('cookies.banner.privacyLink')}
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {t('cookies.banner.rejectAll')}
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {t('cookies.banner.settings')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                {t('cookies.banner.acceptAll')}
              </button>
            </div>
          </div>
        ) : (
          // Detailed view
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('cookies.settings.title')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('cookies.settings.description')}
              </p>
            </div>

            <div className="space-y-3">
              {/* Necessary cookies - always enabled */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="mt-1 w-4 h-4 text-blue-600 rounded cursor-not-allowed opacity-75"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('cookies.categories.necessary.title')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('cookies.categories.necessary.description')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {t('cookies.categories.necessary.cookies')}
                  </p>
                </div>
              </div>

              {/* Functional cookies */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <input
                  type="checkbox"
                  checked={selectedCategories.functional}
                  onChange={() => toggleCategory('functional')}
                  className="mt-1 w-4 h-4 text-blue-600 rounded cursor-pointer"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('cookies.categories.functional.title')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('cookies.categories.functional.description')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {t('cookies.categories.functional.cookies')}
                  </p>
                </div>
              </div>

              {/* Analytics cookies */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <input
                  type="checkbox"
                  checked={selectedCategories.analytics}
                  onChange={() => toggleCategory('analytics')}
                  className="mt-1 w-4 h-4 text-blue-600 rounded cursor-pointer"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {t('cookies.categories.analytics.title')}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t('cookies.categories.analytics.description')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {t('cookies.categories.analytics.cookies')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {t('cookies.settings.back')}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                {t('cookies.banner.rejectAll')}
              </button>
              <button
                onClick={handleAcceptSelected}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                {t('cookies.settings.saveSelected')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                {t('cookies.banner.acceptAll')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Export for backwards compatibility
export function hasMetricsConsent() {
  return cookieService.isCategoryAllowed(COOKIE_CATEGORIES.ANALYTICS);
}